import { useEffect, useRef, useState } from "react";

interface SignaturePadProps {
  onChange: (svg: string | null) => void;
  height?: number;
  penColor?: string;
}

export function SignaturePad({
  onChange,
  height = 200,
  penColor = "#0f172a",
}: SignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasContent, setHasContent] = useState(false);
  const pathsRef = useRef<Array<Array<{ x: number; y: number }>>>([]);
  const currentPathRef = useRef<Array<{ x: number; y: number }>>([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = container.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${height}px`;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = 2.2;
    ctx.strokeStyle = penColor;

    redraw();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [height, penColor]);

  const redraw = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);
    ctx.strokeStyle = penColor;

    for (const path of pathsRef.current) {
      if (path.length < 2) continue;
      ctx.beginPath();
      ctx.moveTo(path[0].x, path[0].y);
      for (let i = 1; i < path.length; i++) {
        ctx.lineTo(path[i].x, path[i].y);
      }
      ctx.stroke();
    }
  };

  const getPos = (e: React.PointerEvent | PointerEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  };

  const start = (e: React.PointerEvent) => {
    e.preventDefault();
    setIsDrawing(true);
    const pos = getPos(e);
    currentPathRef.current = [pos];
    pathsRef.current.push(currentPathRef.current);
  };

  const move = (e: React.PointerEvent) => {
    if (!isDrawing) return;
    e.preventDefault();
    const pos = getPos(e);
    currentPathRef.current.push(pos);
    redraw();
  };

  const end = (e: React.PointerEvent) => {
    if (!isDrawing) return;
    e.preventDefault();
    setIsDrawing(false);
    if (currentPathRef.current.length > 0) {
      setHasContent(true);
      emitSvg();
    }
  };

  const emitSvg = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const svg = buildSvg();
    onChange(svg);
  };

  const buildSvg = (): string => {
    const canvas = canvasRef.current;
    if (!canvas) return "";
    const dpr = window.devicePixelRatio || 1;
    const width = canvas.width / dpr;
    const heightPx = canvas.height / dpr;

    const lines = pathsRef.current
      .filter((p) => p.length >= 2)
      .map((path) => {
        const points = path
          .map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`)
          .join(" ");
        return `<path d="${points}" fill="none" stroke="${penColor}" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>`;
      })
      .join("");

    return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${heightPx}" viewBox="0 0 ${width} ${heightPx}">${lines}</svg>`;
  };

  const clear = () => {
    pathsRef.current = [];
    currentPathRef.current = [];
    setHasContent(false);
    redraw();
    onChange(null);
  };

  return (
    <div>
      <div
        ref={containerRef}
        className="relative bg-white border-2 border-dashed border-slate-300 rounded-lg overflow-hidden"
        style={{ height }}
      >
        <canvas
          ref={canvasRef}
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerLeave={end}
          className="absolute inset-0 touch-none cursor-crosshair"
        />
        {!hasContent && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <p className="text-sm text-slate-400">Dibuja tu firma aquí</p>
          </div>
        )}
      </div>
      <div className="flex justify-between items-center mt-2">
        <p className="text-xs text-slate-400">
          Usa el mouse o el dedo para firmar
        </p>
        {hasContent && (
          <button
            type="button"
            onClick={clear}
            className="text-xs text-red-600 hover:underline font-medium"
          >
            Limpiar
          </button>
        )}
      </div>
    </div>
  );
}